import type { FC } from 'react';
import { Link, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as ReactRouterLink } from 'react-router-dom';
import { MessageDescriptorPropType } from './types';
import type { LinkGroupProps } from './types';

export const StackedLinkGroup: FC<LinkGroupProps> = ({
    title,
    links,
    containerDataTestId,
    headerDataTestId,
}) => {
    const intl = useIntl();

    return (
        <div data-testid={containerDataTestId}>
            <Text
                color="slt-black.900"
                fontFamily="Lato,sans-serif"
                fontSize="1rem"
                fontWeight="700"
                lineHeight="1.1875rem"
                textTransform="uppercase"
                data-testid={headerDataTestId}
            >
                <FormattedMessage {...title} />
            </Text>
            <ul className="mt-[1.63rem]">
                {links.map((link) => (
                    <li key={link.id} className="mt-[1.56rem]">
                        <Link
                            /** When isPwaAccountRedirectEnabled is cleaned, this Link should be a SoftLink (for consistency) */
                            {...(link.isPwaLink
                                ? { as: ReactRouterLink, to: link.url }
                                : { href: link.url })}
                            id={link.componentId ?? undefined}
                            title={intl.formatMessage(link.title)}
                            data-testid={link.dataTestId}
                            fontFamily="Lato,sans-serif"
                            fontSize="0.75rem"
                            fontWeight="400"
                            letterSpacing="0.0625rem"
                            textTransform="uppercase"
                            target={link.showInNewTab ? '_blank' : undefined}
                            {...(!link.url ? { rel: 'nofollow' } : null)}
                            onClick={link.componentId ? (e): void => e.preventDefault() : undefined}
                        >
                            <FormattedMessage {...link.label} />
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
};

StackedLinkGroup.propTypes = {
    title: MessageDescriptorPropType.isRequired,
    links: PropTypes.arrayOf(
        PropTypes.exact({
            id: PropTypes.number.isRequired,
            url: PropTypes.string.isRequired,
            title: MessageDescriptorPropType.isRequired,
            label: MessageDescriptorPropType.isRequired,
            dataTestId: PropTypes.string,
            componentId: PropTypes.string,
            showInNewTab: PropTypes.bool,
            isPwaLink: PropTypes.bool.isRequired,
        }).isRequired,
    ).isRequired,
    containerDataTestId: PropTypes.string,
    headerDataTestId: PropTypes.string,
};

StackedLinkGroup.defaultProps = {
    containerDataTestId: undefined,
    headerDataTestId: undefined,
};
